/**
 * MAISON — CART UPSELL
 *
 * Tabbed product carousels for the cart drawer. One Swiper per category, created lazily the first
 * time its tab is shown so an unopened tab costs nothing.
 *
 * Swiper is fetched from a CDN once per page, shared by every carousel on it.
 *
 * Note on the cart drawer: when the cart changes, theme.js replaces the children of
 * [slot="footer"] wholesale. This element is inside that slot, so it is disconnected and a fresh
 * copy is connected in its place — which is why all state lives on the element instance and
 * initialisation happens in connectedCallback rather than once per page.
 */
(() => {
  if (customElements.get('maison-cart-upsell')) {
    return;
  }

  // Swiper is fetched once per page, no matter how many carousels are on it.
  let swiperPromise = null;

  const loadSwiper = (jsUrl, cssUrl) => {
    if (window.Swiper) {
      return Promise.resolve(window.Swiper);
    }

    if (swiperPromise) {
      return swiperPromise;
    }

    swiperPromise = new Promise((resolve, reject) => {
      if (cssUrl && !document.querySelector(`link[href="${cssUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
      }

      const script = document.createElement('script');
      script.src = jsUrl;
      script.onload = () => resolve(window.Swiper);
      script.onerror = () => reject(new Error('Swiper failed to load'));
      document.head.appendChild(script);
    });

    return swiperPromise;
  };

  customElements.define(
    'maison-cart-upsell',
    class MaisonCartUpsell extends HTMLElement {
      connectedCallback() {
        this.swipers = new Map();
        this.activeId = null;

        this.tabs = Array.from(this.querySelectorAll('[data-upsell-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-upsell-panel]'));
        this.prev = this.querySelector('[data-upsell-prev]');
        this.next = this.querySelector('[data-upsell-next]');

        if (!this.panels.length) {
          return;
        }

        this.bindTabs();
        this.bindArrows();

        // The first visible panel is the active one on render.
        const firstVisible = this.panels.find((panel) => !panel.hasAttribute('hidden'));
        this.activeId = firstVisible ? firstVisible.dataset.upsellPanel : this.panels[0].dataset.upsellPanel;

        // Only fetch Swiper once the block is close to the viewport. The drawer is usually
        // offscreen until opened, so this defers the cost until it matters.
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.observer.disconnect();
              this.initActive();
            }
          },
          { rootMargin: '200px' }
        );

        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();

        // Swiper attaches listeners and observers of its own; destroying it on disconnect stops
        // those leaking every time the drawer footer is re-rendered.
        this.swipers.forEach((swiper) => swiper.destroy(true, true));
        this.swipers.clear();
      }

      bindTabs() {
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab.dataset.upsellTab));

          // Left/right arrows move between tabs, which is what a tablist is expected to do.
          tab.addEventListener('keydown', (event) => {
            const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;

            if (!step) {
              return;
            }

            event.preventDefault();
            const index = this.tabs.indexOf(tab);
            const target = this.tabs[(index + step + this.tabs.length) % this.tabs.length];
            target.focus();
            this.select(target.dataset.upsellTab);
          });
        });
      }

      bindArrows() {
        this.prev?.addEventListener('click', () => this.swipers.get(this.activeId)?.slidePrev());
        this.next?.addEventListener('click', () => this.swipers.get(this.activeId)?.slideNext());
      }

      select(id) {
        if (!id || id === this.activeId) {
          return;
        }

        this.activeId = id;

        this.tabs.forEach((tab) => {
          const selected = tab.dataset.upsellTab === id;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          panel.toggleAttribute('hidden', panel.dataset.upsellPanel !== id);
        });

        this.initActive();
      }

      initActive() {
        const panel = this.panels.find((item) => item.dataset.upsellPanel === this.activeId);

        if (!panel) {
          return;
        }

        const existing = this.swipers.get(this.activeId);

        if (existing) {
          // A Swiper measured while its panel was hidden has zero width, so it needs a
          // re-measure once the panel is visible again.
          existing.update();
          this.syncArrows(existing);
          return;
        }

        const jsUrl = this.dataset.swiperJs;

        if (!jsUrl) {
          return;
        }

        loadSwiper(jsUrl, this.dataset.swiperCss)
          .then((Swiper) => {
            if (!Swiper || !this.isConnected) {
              return;
            }

            this.build(Swiper, panel);
          })
          .catch(() => {
            // Swiper failed to load; the panel stays a plain scrollable row, which is usable.
          });
      }

      build(Swiper, panel) {
        const id = panel.dataset.upsellPanel;

        if (this.swipers.has(id)) {
          return;
        }

        const carousel = panel.querySelector('[data-upsell-carousel]');

        if (!carousel) {
          return;
        }

        const dots = panel.querySelector('[data-upsell-dots]');

        const swiper = new Swiper(carousel, {
          slidesPerView: parseFloat(this.dataset.slidesMobile) || 1.1,
          spaceBetween: this.gap(),
          speed: parseInt(this.dataset.speed, 10) || 500,
          grabCursor: true,
          watchOverflow: true,
          a11y: false,
          breakpoints: {
            1000: {
              slidesPerView: parseFloat(this.dataset.slidesDesktop) || 1.15,
              spaceBetween: this.gap()
            }
          },
          pagination: dots
            ? {
                el: dots,
                clickable: true,
                bulletClass: 'maison-cart-upsell__dot',
                bulletActiveClass: 'maison-cart-upsell__dot--active'
              }
            : false,
          on: {
            init: (instance) => this.syncArrows(instance),
            slideChange: (instance) => this.syncArrows(instance),
            resize: (instance) => {
              instance.params.spaceBetween = this.gap();
              instance.update();
            },
            // reachBeginning/reachEnd alone miss the case where the row does not overflow
            // at all, so the arrow state is recomputed from the instance each time.
            fromEdge: (instance) => this.syncArrows(instance),
            toEdge: (instance) => this.syncArrows(instance)
          }
        });

        this.swipers.set(id, swiper);
      }

      syncArrows(swiper) {
        if (!swiper || swiper.destroyed) {
          return;
        }

        // A row that fits entirely has nothing to page through, so both arrows read as spent.
        const locked = swiper.isBeginning && swiper.isEnd;

        this.prev?.toggleAttribute('data-spent', swiper.isBeginning);
        this.next?.toggleAttribute('data-spent', locked || swiper.isEnd);
      }

      gap() {
        const desktop = window.matchMedia('(min-width: 1000px)').matches;
        const value = desktop ? this.dataset.gapDesktop : this.dataset.gapMobile;

        return parseInt(value, 10) || 0;
      }
    }
  );
})();
